import React, { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import styled from "styled-components";
import { Button, Spinner } from "@cinesys/ui/components";
import { StrategyPill } from "./ui";
import { ExecutionPlan, miQueryKeys, useExecuteImportProfile } from "./service";

/**
 * Manual-run confirmation for a live Collection Sync profile. Opening it asks the manager for the
 * exact write plan (commit=false — no write); "Confirm & Run" asks it to write (commit=true, still
 * behind the manager's own writes-enabled gate). The summary is counts + per-field strategies;
 * internal asset IDs stay behind the "Show affected assets" detail toggle.
 */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 50;
`;

const Card = styled.div`
  background: var(--miSurface);
  border: 1px solid var(--miLine);
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.4);
  width: 100%;
  max-width: 560px;
  max-height: 88vh;
  overflow: auto;
  padding: 24px 26px;
`;

const Title = styled.h2`
  margin: 0 0 4px;
  font-size: 18px;
  color: #fff;
`;

const Sub = styled.p`
  margin: 0 0 18px;
  font-size: 13px;
  color: var(--miInkFaint);
`;

const Counts = styled.div`
  display: flex;
  gap: 26px;
  margin: 6px 0 14px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--miInkFaint);

  b {
    display: block;
    font-size: 22px;
    margin-bottom: 3px;
    color: #fff;
    font-variant-numeric: tabular-nums;
  }
`;

const StratRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 18px;

  div {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: var(--miInkSoft);
  }
`;

const Note = styled.p<{ tone?: "warn" | "ok" | "danger" }>`
  margin: 14px 0 0;
  font-size: 13px;
  color: ${(p) =>
    p.tone === "danger"
      ? "var(--miDanger)"
      : p.tone === "ok"
      ? "var(--miAccentBright)"
      : p.tone === "warn"
      ? "var(--miWarn)"
      : "var(--miInkFaint)"};
`;

const Detail = styled.details`
  margin: 8px 0 4px;
  font-size: 13px;
  color: var(--miInkSoft);

  summary {
    cursor: pointer;
    color: var(--miAccentBright);
  }
  ul {
    margin: 10px 0 0;
    padding-left: 18px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  code {
    color: var(--miInkFaint);
    font-size: 11px;
  }
`;

const Foot = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 22px;
`;

function PlanSummary({ plan }: { plan: ExecutionPlan }) {
  const allMatch = plan.assetsToWrite === 0;
  return (
    <>
      <Counts>
        <div>
          <b>{plan.assetsToWrite}</b>
          Affected assets
        </div>
        <div>
          <b>{plan.fieldChanges}</b>
          Proposed field changes
        </div>
      </Counts>
      <StratRow>
        {plan.strategies.map((s) => (
          <div key={s.fieldName}>
            {s.fieldName} <StrategyPill strategy={s.strategy} />
          </div>
        ))}
      </StratRow>
      {allMatch ? (
        <Note tone="ok">
          All {plan.targeted} asset{plan.targeted === 1 ? "" : "s"} already
          match the configured metadata. No write is required.
        </Note>
      ) : (
        plan.unchanged > 0 && (
          <Note>{plan.unchanged} asset(s) already match — no change.</Note>
        )
      )}
      {plan.blocked.length > 0 && (
        <Note tone="warn">
          {plan.blocked.length} asset(s) blocked by validation and will be
          skipped.
        </Note>
      )}
      {!allMatch && (
        <Detail>
          <summary>Show affected assets</summary>
          <ul>
            {plan.items.map((it) => (
              <li key={it.assetId}>
                {it.assetTitle} <code>{it.assetId}</code> —{" "}
                {it.fields.map((f) => f.fieldName).join(", ")}
              </li>
            ))}
          </ul>
        </Detail>
      )}
    </>
  );
}

export default function RunConfirmModal({
  profileId,
  onClose,
}: {
  profileId: string;
  onClose: () => void;
}) {
  const execute = useExecuteImportProfile(profileId);
  const qc = useQueryClient();
  const [plan, setPlan] = useState<ExecutionPlan | null>(null);
  const [startedJobId, setStartedJobId] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Establish the plan to review on open (commit=false — authoritative recompute, no write). The
  // fingerprints this returns are exactly what "Confirm & Run" binds the run to.
  useEffect(() => {
    let live = true;
    execute
      .mutateAsync({ commit: false })
      .then((res) => {
        if (!live) {
          return;
        }
        // commit:false always returns the review plan (ExecutionOutcome).
        if ("plan" in res) {
          if (res.status === "stale") {
            setStale(true);
          } else {
            setPlan(res.plan);
          }
        }
      })
      .catch(() => live && setError("Could not compute the run plan."));
    return () => {
      live = false;
    };
  }, []);

  const confirm = async () => {
    if (!plan) {
      return;
    }
    try {
      // Bind the run to the EXACT reviewed plan: both the config revision and the per-asset
      // planRevision. If either drifted since this modal opened, the worker returns stale — no write.
      // commit:true starts a Manager Job and returns its id; the outcome is tracked in Run history.
      const res = await execute.mutateAsync({
        reviewedRevision: plan.revision,
        reviewedPlanRevision: plan.planRevision,
        commit: true,
      });
      if ("jobId" in res) {
        setStartedJobId(res.jobId);
        // Refresh Run history so the new Job appears.
        qc.invalidateQueries(miQueryKeys.runs(profileId));
      }
    } catch {
      setError("The run failed to start.");
    }
  };

  const committing = execute.isLoading && !!plan && !startedJobId;

  return (
    <Overlay onClick={onClose}>
      <Card onClick={(e) => e.stopPropagation()}>
        <Title>Run Collection Sync</Title>
        <Sub>Review the exact changes before writing to iconik.</Sub>

        {error ? (
          <Note tone="danger">{error}</Note>
        ) : stale ? (
          <Note tone="warn">
            The source or configuration changed since this preview. Close,
            refresh the preview, and review again before running.
          </Note>
        ) : !plan ? (
          <Spinner padTop />
        ) : (
          <>
            <PlanSummary plan={plan} />
            {startedJobId && (
              <Note tone="ok">
                Run started — track its result in Run history. The run
                reconciles the current state and writes only what changed.
              </Note>
            )}
          </>
        )}

        <Foot>
          <Button label="Close" type="default" onClick={onClose} />
          {plan && !startedJobId && !stale && (
            <Button
              label={committing ? "Starting…" : "Confirm & Run"}
              type="success"
              disabled={committing || plan.assetsToWrite === 0}
              onClick={confirm}
            />
          )}
        </Foot>
      </Card>
    </Overlay>
  );
}
